import React from 'react';

import type { MediaAttachment } from 'widget/types/attachments';
import { downloadFile } from 'widget/utils/downloadFile';

import { NavigationDirection } from '../constants';

export type UseMediaViewerArgs = {
  media: MediaAttachment[];
  initialIndex: number;
  onError?: (error: Error) => void;
  onClose: () => void;
};

export type UseMediaViewerReturn = {
  currentIndex: number;
  currentMedia: MediaAttachment;
  displayedMedia: MediaAttachment;
  hasMultiple: boolean;
  isMediaVisible: boolean;
  handleGoToNext: () => void;
  handleGoToPrev: () => void;
  handleTransitionEnd: () => void;
  handleDownload: () => void;
  handleOverlayClick: (event: React.MouseEvent<HTMLDivElement>) => void;
  overlayRef: React.RefObject<HTMLDivElement>;
};

const useMediaViewer = ({
  media,
  initialIndex,
  onClose,
  onError
}: UseMediaViewerArgs): UseMediaViewerReturn => {
  const [currentIndex, setCurrentIndex] = React.useState(initialIndex);
  const [isMediaVisible, setIsMediaVisible] = React.useState(true);

  const displayedIndexRef = React.useRef(initialIndex);
  const pendingIndexRef = React.useRef<number | null>(null);
  const safetyTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null
  );
  const overlayRef = React.useRef<HTMLDivElement | null>(null);

  const hasMultiple = media.length > 1;
  const currentMedia = media[currentIndex];
  const displayedMedia = media[displayedIndexRef.current];

  const navigateToSlide = React.useCallback(
    (direction: NavigationDirection) => {
      if (pendingIndexRef.current !== null) {
        return;
      }

      const currentIndex = displayedIndexRef.current;
      const newIndex =
        direction === NavigationDirection.NEXT
          ? (currentIndex + 1) % media.length
          : (currentIndex - 1 + media.length) % media.length;

      pendingIndexRef.current = newIndex;
      setCurrentIndex(newIndex);
      setIsMediaVisible(false);

      safetyTimeoutRef.current = setTimeout(() => {
        if (pendingIndexRef.current === null) {
          return;
        }
        displayedIndexRef.current = pendingIndexRef.current;
        pendingIndexRef.current = null;
        safetyTimeoutRef.current = null;
        setIsMediaVisible(true);
      }, 150);
    },
    [media.length]
  );

  const handleTransitionEnd = React.useCallback(() => {
    if (pendingIndexRef.current === null) {
      return;
    }
    if (safetyTimeoutRef.current !== null) {
      clearTimeout(safetyTimeoutRef.current);
      safetyTimeoutRef.current = null;
    }
    displayedIndexRef.current = pendingIndexRef.current;
    pendingIndexRef.current = null;
    setIsMediaVisible(true);
  }, []);

  const handleGoToNext = React.useCallback(() => {
    navigateToSlide(NavigationDirection.NEXT);
  }, [navigateToSlide]);

  const handleGoToPrev = React.useCallback(() => {
    navigateToSlide(NavigationDirection.PREV);
  }, [navigateToSlide]);

  const handleDownload = React.useCallback(async () => {
    if (!currentMedia) {
      return;
    }

    downloadFile({
      url: currentMedia.url,
      filename: currentMedia.filename,
      onError
    });
  }, [currentMedia, onError]);

  const handleOverlayClick = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  const handleKeyDown = React.useCallback(
    (event: KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          onClose();
          break;

        case 'ArrowLeft':
          if (hasMultiple) {
            handleGoToPrev();
          }
          break;
        case 'ArrowRight':
          if (hasMultiple) {
            handleGoToNext();
          }
          break;
      }
    },
    [onClose, hasMultiple, handleGoToPrev, handleGoToNext]
  );

  React.useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  React.useEffect(() => {
    return () => {
      if (safetyTimeoutRef.current !== null) {
        clearTimeout(safetyTimeoutRef.current);
      }
    };
  }, []);

  return {
    currentIndex,
    currentMedia,
    displayedMedia,
    hasMultiple,
    isMediaVisible,
    handleGoToNext,
    handleGoToPrev,
    handleTransitionEnd,
    handleDownload,
    handleOverlayClick,
    overlayRef
  };
};

export default useMediaViewer;
